import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Badge } from "@/components/ui/badge";
import { InvoiceStats } from "@/components/invoices/InvoiceStats";
import { InvoiceFilters } from "@/components/invoices/InvoiceFilters";
import { InvoiceTableSkeleton } from "@/components/invoices/InvoiceTableSkeleton";
import { InvoiceDetailsModal } from "@/components/invoices/InvoiceDetailsModal";

import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ChevronRight,
  Eye,
  Download,
  Calendar,
  ArrowUp,
  ArrowDown,
  MoreHorizontal,
  FileText,
} from "lucide-react";
import { downloadResponseFile } from "@/utils/common.utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EnhancedPaginationControl } from "@/components/common/EnhancedPaginationControl";
import { InvoiceApi, InvoiceVO } from "@/api/invoice.api";
import { ProgramApi } from "@/api/program.api";
import {
  addDays,
  startOfDay,
  endOfDay,
  startOfMonth,
  endOfMonth,
  subMonths,
  subDays,
} from "date-fns";
import { getFormattedDateTime } from "@/utils";
import type { UserInfo } from "@/hooks/use-user-info";
import {
  fetchMerchantsForUserFilter,
  getDefaultMerchantFilterCode,
  getScopedMerchantId,
} from "@/utils/merchant-filter.utils";

interface TransformedInvoice {
  id: string;
  merchant: string;
  program: string;
  fullMerchantName?: string;
  fullProgramName?: string;
  issueDate: string;
  dueDate: string;
  amount: number;
  currencyCode?: string;
  status: string;
  invoiceType?: "program" | "merchant" | "unknown";
  programName?: string;
  merchantName?: string;
  memberName: string;
  memberId: string;
  memberEmail: string;
  merchantAddress: string;
  merchantEmail: string;
  paymentMethod: string;
  subtotal: number;
  taxRate: number;
  tax: number;
  total: number;
  startDate: string | null;
  endDate: string | null;
  transactions: Array<{
    id: string;
    date: string;
    description: string;
    points: number;
    amount: number;
  }>;
}

const Invoices = () => {
  const userInfo: UserInfo | null = (() => {
    try {
      const stored = sessionStorage.getItem("userInfo");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  })();
  const scopedMerchantId = getScopedMerchantId(userInfo);
  const defaultMerchantCode = getDefaultMerchantFilterCode(userInfo);

  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState<{
    status?: string;
    programCode?: string;
    merchantCode?: string;
    id?: string;
    startDate?: string;
    endDate?: string;
  }>(() =>
    defaultMerchantCode ? { merchantCode: defaultMerchantCode } : {}
  );
  const [dateRange, setDateRange] = useState("none");
  const [sortBy, setSortBy] = useState({
    field: "createdDate",
    direction: "desc",
  });
  const [selectedInvoice, setSelectedInvoice] =
    useState<TransformedInvoice | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [invoices, setInvoices] = useState<TransformedInvoice[]>([]);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [invoiceStats, setInvoiceStats] = useState({
    totalInvoices: 0,
    totalPaid: "$0.00",
    pendingAmount: "$0.00",
    overdueAmount: "$0.00",
    averagePaymentTime: 0,
    totalPaidInvoices: 0,
    totalPendingInvoices: 0,
    totalValueOfAllInvoices: "0",
  });
  const [programs, setPrograms] = useState<any[]>([]);
  const [merchants, setMerchants] = useState<any[]>([]);

  // Helper function for currency formatting: show as "amount CODE" (e.g., 200 AED)
  const formatAmountWithCode = (
    value: number,
    currencyCode: string = "USD",
  ) => {
    const truncated = Math.floor((Number(value) || 0) * 100) / 100;
    const formatted = truncated.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    return `${formatted} ${currencyCode || "USD"}`.trim();
  };

  // Backward-compatible wrapper used elsewhere in this file
  const formatCurrency = (value: number, currencyCode: string = "USD") =>
    formatAmountWithCode(value, currencyCode);

  // Helper function for number formatting
  const formatNumber = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Helper function for date formatting
  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    return getFormattedDateTime(dateString) || "N/A";
  };

  // Helper to get start and end in millis for a given range
  const getDateRangeMillis = (range) => {
    const now = new Date();
    let start, end;
    switch (range) {
      case "30days":
        start = startOfDay(addDays(now, -29));
        end = endOfDay(now);
        break;
      case "7days":
        start = startOfDay(addDays(now, -6));
        end = endOfDay(now);
        break;
      case "today":
        start = startOfDay(now);
        end = endOfDay(now);
        break;
      case "yesterday":
        start = startOfDay(addDays(now, -1));
        end = endOfDay(addDays(now, -1));
        break;
      case "thisMonth":
        start = startOfMonth(now);
        end = endOfDay(now);
        break;
      case "lastMonth":
        start = startOfMonth(subMonths(now, 1));
        end = endOfMonth(subMonths(now, 1));
        break;
      case "none":
        start = null;
        end = null;
        break;
      default:
        start = null;
        end = null;
    }
    return {
      startDateInMillis:
        range === "none" ? null : start ? start.getTime() : null,
      endDateInMillis: range === "none" ? null : end ? end.getTime() : null,
    };
  };

  const fetchInvoices = async () => {
    try {
      setIsLoading(true);
      let startDateInMillis = null;
      let endDateInMillis = null;
      if (filters.startDate && filters.endDate) {
        startDateInMillis = startOfDay(new Date(filters.startDate)).getTime();
        endDateInMillis = endOfDay(new Date(filters.endDate)).getTime();
      } else {
        const range = getDateRangeMillis(dateRange);
        startDateInMillis = range.startDateInMillis;
        endDateInMillis = range.endDateInMillis;
      }
      const response = await InvoiceApi.getInvoicesFromDB({
        invoiceType: "earn",
        pageNumber: currentPage,
        pageSize: rowsPerPage,
        sortBy: sortBy.field,
        sortOrder: sortBy.direction as "asc" | "desc",
        status:
          filters.status && filters.status !== "all"
            ? filters.status
            : undefined,
        programCode:
          filters.programCode && filters.programCode !== "all"
            ? filters.programCode
            : undefined,
        merchantCode:
          filters.merchantCode && filters.merchantCode !== "all"
            ? filters.merchantCode
            : defaultMerchantCode,
        merchantId: scopedMerchantId,
        id: filters.id || undefined,
        startDateInMillis,
        endDateInMillis,
      });

      if (response.data) {
        // Transform API data to match the expected format
        const transformedInvoices: TransformedInvoice[] =
          response.data.invoiceVOs.map((invoice) => {
            // Determine invoice from/to based on business rules
            let invoiceFrom = "N/A";
            let invoiceTo = "N/A";

            const hasMerchant = !!invoice.merchant;
            const programVOFromTx =
              invoice.merchantTransactionVOS?.[0]?.programVO;
            const hasProgram = !hasMerchant && !!programVOFromTx;

            if (hasMerchant) {
              // If merchant exists, LOYYAL is raising invoice to merchant
              invoiceFrom = "Loyyal Technologies";
              invoiceTo = invoice.merchant.merchantName || "N/A";
            } else if (hasProgram) {
              // If program exists (and merchant doesn't), program is raising invoice to LOYYAL
              invoiceFrom = programVOFromTx.programName || "N/A";
              invoiceTo = "Loyyal Technologies";
            }

            // Format invoice from/to to show only first two words
            const formatText = (text: string) => {
              const words = text.split(" ");
              if (words.length > 2) {
                return `${words.slice(0, 2).join(" ")}...`;
              }
              return text;
            };

            // Determine currency code: use merchant.currencyVO if merchant exists,
            // otherwise use program.currencyVO if program exists, fallback to USD
            const currencyCode =
              invoice?.merchant?.currencyVO?.iso3CurrencyCode ||
              invoice?.program?.currencyVO?.iso3CurrencyCode ||
              "USD";

            return {
              id: `#${invoice.id}`, // Format invoice ID
              merchant: formatText(invoiceFrom), // Use invoice from as merchant field
              program: formatText(invoiceTo), // Use invoice to as program field
              fullMerchantName: invoiceFrom, // Store full merchant name for hover
              fullProgramName: invoiceTo, // Store full program name for hover
              issueDate: invoice.createdDate || "N/A",
              dueDate: invoice.dueDate || "N/A",
              // Use amountAfterTaxes for the Amount column in the table
              amount: invoice.amountAfterTaxes || invoice.amount || 0,
              currencyCode,
              invoiceType: hasProgram
                ? "program"
                : hasMerchant
                  ? "merchant"
                  : "unknown",
              programName:
                programVOFromTx?.programName ||
                invoice?.program?.programName ||
                "",
              merchantName: invoice?.merchant?.merchantName || "",
              status: (invoice.status || "PENDING").toLowerCase(),
              memberName: invoice.merchantTransactionVOS?.[0]?.memberVO
                ? `${
                    invoice.merchantTransactionVOS[0].memberVO.firstName || ""
                  } ${
                    invoice.merchantTransactionVOS[0].memberVO.lastName || ""
                  }`.trim() || "N/A"
                : "N/A",
              memberId:
                invoice.merchantTransactionVOS?.[0]?.memberVO
                  ?.programMemberId || "N/A",
              memberEmail:
                invoice.merchantTransactionVOS?.[0]?.memberVO
                  ?.personalEmailAddress || "N/A",
              merchantAddress: invoice.merchant?.businessLocation || "N/A",
              merchantEmail: invoice.merchant?.merchantEmail || "N/A",
              paymentMethod: "N/A", // Not available in API response
              subtotal: invoice.amount || 0,
              taxRate: invoice.taxPercentage || 0,
              tax: (invoice.amountAfterTaxes || 0) - (invoice.amount || 0),
              total: invoice.amountAfterTaxes || 0,
              startDate: invoice.startDate || null,
              endDate: invoice.endDate || null,
              transactions: (invoice.merchantTransactionVOS || []).map(
                (tx) => ({
                  id: tx.id != null ? String(tx.id) : "N/A",
                  date: tx.transactionDate || "N/A",
                  cashbackPercentage: tx.transactionCashbackPercentage || 0,
                  points: tx.points || 0,
                  amount: tx.transactionCashbackAmount || 0,
                  amountAfterTxnFee:
                    tx.pointsEquivalentAmountAfterAdjustingLoyyalCommision || 0,
                }),
              ),
            };
          });
        setInvoices(transformedInvoices);
        setTotalInvoices(Number(response.data.totalNumberOfInvoices) || 0); // Set total invoices
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
      toast.error("Failed to fetch invoices");
    } finally {
      setIsLoading(false);
    }
  };

  const totalPages = Math.ceil(totalInvoices / rowsPerPage); // Calculate totalPages here

  useEffect(() => {
    fetchInvoices();
  }, [currentPage, rowsPerPage, sortBy, filters, dateRange]);

  useEffect(() => {
    const fetchInvoiceStats = async () => {
      try {
        const response = await InvoiceApi.getInvoiceStatisticsFromDB({
          invoiceType: "earn",
          merchantId: scopedMerchantId,
        });
        if (response.data) {
          const currencyCode =
            response.data.currencyVO?.iso3CurrencyCode || "";
          const formatStatAmount = (value: number) => {
            const truncated = Math.floor((Number(value) || 0) * 100) / 100;
            const formatted = truncated.toLocaleString("en-US", {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            });
            return currencyCode
              ? `${formatted} ${currencyCode}`
              : formatted;
          };
          setInvoiceStats({
            totalInvoices: Number(response.data.totalNumberOfInvoices || 0),
            totalPaid: formatStatAmount(response.data.totalAmountPaid || 0),
            pendingAmount: formatStatAmount(
              response.data.totalAmountPendingAndOverdue || 0,
            ),
            overdueAmount: formatStatAmount(0), // Not provided in API, set to 0
            averagePaymentTime: 0, // Not provided in API, set to 0
            totalPaidInvoices: Number(
              response.data.totalNumberOfPaidInvoices || 0,
            ),
            totalPendingInvoices: Number(
              response.data.totalNumberOfPendingAndOverdueInvoices || 0,
            ),
            totalValueOfAllInvoices: formatStatAmount(
              response.data.totalValueOfAllInvoices || 0,
            ),
          });
        }
      } catch (error) {
        console.error("Error fetching invoice statistics:", error);
        toast.error("Failed to fetch invoice statistics");
      }
    };

    fetchInvoiceStats();
  }, []);

  useEffect(() => {
    // Fetch programs and merchants for filter dropdowns
    const fetchProgramsAndMerchants = async () => {
      try {
        const [merchantList, programRes] = await Promise.all([
          fetchMerchantsForUserFilter(userInfo),
          ProgramApi.getProgramsFromDB({
            pageNumber: 0,
            pageSize: 100,
            sortOrder: "asc",
            sortBy: "programName",
            programCode: null,
            programName: null,
            programType: "EARN",
          }),
        ]);
        setPrograms(
          Array.isArray(programRes.data?.programVOs)
            ? programRes.data.programVOs
            : [],
        );
        setMerchants(merchantList);
      } catch (e) {
        setPrograms([]);
        setMerchants([]);
      }
    };
    fetchProgramsAndMerchants();
  }, []);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(0);
    // In a real app, we would filter the data based on the search term
  };

  const handleFilterChange = (newFilters: any) => {
    // If a filter is set to 'all', remove it from the filters object
    const merged = { ...filters, ...newFilters };
    Object.keys(merged).forEach((key) => {
      if (merged[key] === "all") delete merged[key];
    });
    setFilters(merged);
    setCurrentPage(0);
  };

  const handleDateRangeChange = (range: string) => {
    setDateRange(range);
  };

  const handleReset = () => {
    setSearchTerm("");
    setFilters(
      defaultMerchantCode ? { merchantCode: defaultMerchantCode } : {}
    );
    setDateRange("none");
    setSortBy({ field: "createdDate", direction: "desc" });
    setCurrentPage(0);
    toast.success("All filters have been reset");
  };

  const handleSort = (field: string) => {
    if (sortBy.field === field) {
      setSortBy({
        field,
        direction: sortBy.direction === "asc" ? "desc" : "asc",
      });
    } else {
      setSortBy({ field, direction: "asc" });
    }
  };

  const handleViewDetails = (invoice: TransformedInvoice) => {
    setSelectedInvoice(invoice);
    setIsDetailsModalOpen(true);
  };

  const handleDownloadInvoice = async (invoiceId: string) => {
    const cleanInvoiceId = invoiceId.replace("#", "");
    const API_BASE = import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL;
    console.log("Downloading invoice:", cleanInvoiceId);
    try {
      const response = await fetch(
        `${API_BASE}/invoice/v2/generateInvoicePDF?invoiceId=${cleanInvoiceId}`,
        {
          method: "GET",
        },
      );
      if (!response.ok) {
        throw new Error("Failed to download PDF");
      }
      const filename = await downloadResponseFile(
        response,
        `invoice-${cleanInvoiceId}.pdf`,
      );
      toast.success(`Invoice ${filename} downloaded successfully!`);
    } catch (error) {
      console.error("Error downloading invoice:", error);
      toast.error(`Failed to download invoice ${invoiceId}`);
    }
  };

  const handleDownloadInvoiceCSV = async (invoiceId: string) => {
    const cleanInvoiceId = invoiceId.replace("#", "");
    const API_BASE = import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL;
    try {
      const response = await fetch(
        `${API_BASE}/invoice/getTransactionsFromInvoiceInCSVFormat?invoiceId=${cleanInvoiceId}`,
        {
          method: "GET",
        },
      );
      if (!response.ok) {
        throw new Error("Failed to download CSV");
      }
      const filename = await downloadResponseFile(
        response,
        `invoice-${cleanInvoiceId}.csv`,
      );
      toast.success(`Invoice CSV ${filename} downloaded successfully!`);
    } catch (error) {
      console.error("Error downloading invoice CSV:", error);
      toast.error(`Failed to download invoice CSV ${invoiceId}`);
    }
  };

  const handleMarkAsPaid = async (invoiceId: string) => {
    try {
      // Remove # from invoice ID if present
      const cleanInvoiceId = invoiceId.replace("#", "");
      console.log("Marking invoice as paid:", cleanInvoiceId); // Debug log
      const API_BASE = import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL;
      const url = `${API_BASE}/invoice/updateInvoiceStatus?invoiceId=${cleanInvoiceId}&status=PAID`;
      console.log("API URL:", url); // Debug log

      const response = await fetch(url, {
        method: "GET",
        credentials: "include",
      });

      console.log("Response status:", response.status); // Debug log

      if (response.ok) {
        toast.success(`Invoice ${invoiceId} marked as paid successfully!`);
        // Refresh the invoices list to reflect the status change
        fetchInvoices();
      } else {
        const errorText = await response.text();
        console.error("API Error:", errorText); // Debug log
        toast.error(`Failed to mark invoice ${invoiceId} as paid`);
      }
    } catch (error) {
      console.error("Error marking invoice as paid:", error);
      toast.error(`Failed to mark invoice ${invoiceId} as paid`);
    }
  };

  const SortableHeader = ({
    field,
    children,
  }: {
    field: string;
    children: React.ReactNode;
  }) => {
    const isSorted = sortBy.field === field;
    const direction = sortBy.direction;

    return (
      <div
        className="flex items-center cursor-pointer"
        onClick={() => handleSort(field)}
      >
        {children}
        <span className="ml-1">
          {isSorted && direction === "asc" && <ArrowUp className="h-4 w-4" />}
          {isSorted && direction === "desc" && (
            <ArrowDown className="h-4 w-4" />
          )}
          {!isSorted && <ChevronRight className="h-4 w-4 opacity-30" />}
        </span>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Invoices</h1>
        <p className="text-gray-500 mt-1">
          Manage and view all invoices for your loyalty programs
        </p>
      </div>

      {/* Invoice Stats */}
      <InvoiceStats isLoading={isLoading} stats={invoiceStats} />

      {/* Invoice Filters */}
      <InvoiceFilters
        onSearch={handleSearch}
        onFilterChange={handleFilterChange}
        onDateRangeChange={handleDateRangeChange}
        onReset={handleReset}
        programs={programs}
        merchants={merchants}
        defaultMerchantCode={defaultMerchantCode}
        lockMerchantFilter={!!defaultMerchantCode}
      />

      {/* Invoices Table */}
      {isLoading ? (
        <InvoiceTableSkeleton rowCount={rowsPerPage} />
      ) : (
        <DataTable
          title="All Invoices"
          data={invoices}
          columns={[
            {
              header: "Invoice ID",
              accessorKey: "id",
            },
            {
              header: "Invoice From",
              accessorKey: "merchant",
              cell: (row) => {
                const fullText = row.fullMerchantName || row.merchant;
                return <span title={fullText}>{row.merchant}</span>;
              },
            },
            {
              header: "Invoice To",
              accessorKey: "program",
              cell: (row) => {
                const fullText = row.fullProgramName || row.program;
                return <span title={fullText}>{row.program}</span>;
              },
            },
            {
              header: "Create Date",
              accessorKey: "issueDate",
              cell: (row) => (
                <div className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5 text-gray-500" />
                  <span>{formatDate(row.issueDate)}</span>
                </div>
              ),
            },
            {
              header: <SortableHeader field="dueDate">Due Date</SortableHeader>,
              accessorKey: "dueDate",
              cell: (row) => (
                <div className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5 text-gray-500" />
                  <span>{formatDate(row.dueDate)}</span>
                </div>
              ),
            },
            {
              header: <SortableHeader field="amount">Amount</SortableHeader>,
              accessorKey: "amount",
              cell: (row) => (
                <span className="font-medium">
                  {formatAmountWithCode(row.amount, row.currencyCode)}
                </span>
              ),
            },
            {
              header: <SortableHeader field="status">Status</SortableHeader>,
              accessorKey: "status",
              cell: (row) => {
                const statusStyles = {
                  paid: "bg-green-100 text-green-800 hover:bg-green-100",
                  pending: "bg-yellow-100 text-yellow-800 hover:bg-yellow-100",
                  overdue: "bg-red-100 text-red-800 hover:bg-red-100",
                };

                return (
                  <Badge
                    variant="outline"
                    className={
                      statusStyles[row.status as keyof typeof statusStyles]
                    }
                  >
                    {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
                  </Badge>
                );
              },
            },
            {
              header: "Actions",
              id: "actions",
              cell: (row) => (
                <div className="flex justify-center">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <span className="sr-only">Open menu</span>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-white">
                      <DropdownMenuItem onClick={() => handleViewDetails(row)}>
                        <Eye className="mr-2 h-4 w-4" />
                        <span>View Details</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDownloadInvoice(row.id)}
                      >
                        <FileText className="mr-2 h-4 w-4" />
                        <span>Download invoice PDF</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDownloadInvoiceCSV(row.id)}
                      >
                        <Download className="mr-2 h-4 w-4" />
                        <span>Download invoice CSV</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() =>
                          row.status !== "paid" && handleMarkAsPaid(row.id)
                        }
                        disabled={row.status === "paid"}
                      >
                        <span className="mr-2 h-4 w-4">✓</span>
                        <span>Mark as Paid</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ),
            },
          ]}
        />
      )}

      {/* Invoice Details Modal */}
      <InvoiceDetailsModal
        open={isDetailsModalOpen}
        onOpenChange={setIsDetailsModalOpen}
        invoice={selectedInvoice}
      />

      {/* Pagination controls */}
      {!isLoading && totalInvoices > 0 && (
        <div className="mt-4 w-full">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Rows per page:</span>
              <Select
                value={rowsPerPage.toString()}
                onValueChange={(value) => {
                  setRowsPerPage(Number(value));
                  setCurrentPage(0);
                }}
              >
                <SelectTrigger className="w-[80px]">
                  <SelectValue placeholder={rowsPerPage.toString()} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <EnhancedPaginationControl
              totalPages={totalPages}
              currentPage={currentPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
            <div className="w-[160px]" /> {/* Spacer to balance layout */}
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoices;
